import Overlay from "ol/Overlay"
import { statusDisplay } from "./status"

const DOCKED_QUERY = "(max-width: 575.98px)"

// The map's ONLY info DOM node — shared across all devices (no per-device DOM).
// Two presentation modes: anchored ol/Overlay (default) and, on xs viewports,
// a bottom card docked into the controller root (a sibling of the OL viewport,
// so card taps never reach the map's singleclick handler).
export class MarkerInfo {
    #device = null   // device currently shown
    #popupOverlay
    #popupEl
    #dockRoot
    #mediaQuery
    #onMediaChange

    constructor(map, dockRoot) {
        this.#popupEl = document.createElement("div")
        this.#popupEl.className = "tracker-popup"
        this.#popupOverlay = new Overlay({
            element: this.#popupEl,
            positioning: "bottom-center",
            offset: [0, -40],
            autoPan: { animation: { duration: 250 } }
        })
        map.addOverlay(this.#popupOverlay)
        this.#dockRoot = dockRoot
        this.#mediaQuery = window.matchMedia(DOCKED_QUERY)
        // crossing the breakpoint mid-session: close; reopening is a manual tap
        this.#onMediaChange = () => this.close()
        this.#mediaQuery.addEventListener("change", this.#onMediaChange)
    }

    get deviceId() { return this.#device?.id }

    openFor(device) {
        this.#device = device
        this.#renderPopup(device)
        this.#popupEl.classList.remove("d-none")
        if (this.#mediaQuery.matches) {
            this.#popupEl.classList.add("tracker-popup--docked")
            // detach from the overlay first — setElement(#popupEl) later is a no-op unless the value actually changes
            this.#popupOverlay.setElement(undefined)
            this.#popupOverlay.setPosition(undefined)
            if (this.#popupEl.parentElement !== this.#dockRoot) this.#dockRoot.appendChild(this.#popupEl)
        } else {
            this.#popupEl.classList.remove("tracker-popup--docked")
            if (this.#popupOverlay.getElement() !== this.#popupEl) {
                this.#popupEl.remove()
                this.#popupOverlay.setElement(this.#popupEl)
            }
            this.#popupOverlay.setPosition(device.feature.getGeometry().getCoordinates())
        }
    }

    close() {
        this.#device = null
        this.#popupOverlay.setPosition(undefined)
        this.#popupEl.classList.add("d-none")
    }

    dispose() { this.#mediaQuery.removeEventListener("change", this.#onMediaChange) }

    closeIfDevice(id) { if (this.#device?.id === id) this.close() }

    // Re-render + reposition when `device` is the open one; closes instead once it got hidden.
    refresh(device) {
        if (this.#device?.id !== device.id) return
        if (!device.visible) { this.close(); return }
        this.#renderPopup(device)
        if (!this.#mediaQuery.matches) this.#popupOverlay.setPosition(device.feature.getGeometry().getCoordinates())
    }

    #renderPopup(device) {
        const { text, bg, fg } = statusDisplay(device.status)
        const el = this.#popupEl
        el.textContent = ""

        const header = document.createElement("div")
        header.className = "tracker-popup-header"
        const heading = document.createElement("h6")
        heading.textContent = device.name ?? ""
        const close = document.createElement("button")
        close.type = "button"
        close.className = "btn-close"
        close.setAttribute("aria-label", "Schließen")
        close.addEventListener("click", () => this.close())
        header.append(heading, close)
        el.appendChild(header)

        const statusRow = document.createElement("div")
        statusRow.className = "tracker-popup-row"
        const statusLabel = document.createElement("span")
        statusLabel.textContent = "Status"
        const statusValue = document.createElement("span")
        statusValue.className = "tracker-popup-status"
        statusValue.textContent = text
        statusValue.style.backgroundColor = bg
        statusValue.style.color = fg
        statusRow.append(statusLabel, statusValue)
        el.appendChild(statusRow)

        if (device.members.length > 0) {
            device.members.forEach(member => {
                const row = document.createElement("div")
                row.className = "tracker-popup-row tracker-popup-member"
                const memberIssi = document.createElement("span")
                memberIssi.textContent = member.issi
                const memberStatus = document.createElement("span")
                const display = statusDisplay(member.status)
                memberStatus.className = "tracker-popup-status"
                memberStatus.textContent = display.text
                memberStatus.style.backgroundColor = display.bg
                memberStatus.style.color = display.fg
                row.append(memberIssi, memberStatus)
                el.appendChild(row)
            })
        } else {
            const issiRow = document.createElement("div")
            issiRow.className = "tracker-popup-row"
            const issiLabel = document.createElement("span")
            issiLabel.textContent = "ISSI"
            const issiValue = document.createElement("span")
            issiValue.textContent = device.issi
            issiRow.append(issiLabel, issiValue)
            el.appendChild(issiRow)
        }

        const seen = document.createElement("div")
        seen.className = "tracker-popup-seen"
        seen.textContent = MarkerInfo.formatTimestamp(device.positionAt)
        el.appendChild(seen)
    }

    // de-AT :compact_with_seconds — "%d.%m.%Y, %H:%M:%S Uhr"
    static formatTimestamp(date) {
        const pad = n => String(n).padStart(2, "0")
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} Uhr`
    }
}
